import {AfterViewInit, Component, EventEmitter, inject, Input, Output} from '@angular/core';
import * as uuid from "uuid";
import {Device} from "../../../models/device/Device";
import {UserMetricInstance} from "../../../models/user/Metric/UserMetricInstance";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {BaseComponent} from "../../BaseComponent";
import {DeviceOpeService} from "../../../services/ope/deviceOpe.service";
import {GetDevicesMetricInstancesCB} from "../../../ope/device/GetDevicesMetricInstancesCB";
import {AggregateFunctionTypeEnum} from "../../../enums/dashboard/ChartsEnum";
import {GetVirtualMetricInstancesCB} from "../../../ope/virtualmetric/GetVirtualMetricInstancesCB";
import {VirtualmetricOpeService} from "../../../services/ope/virtualmetricOpe.service";
import {DataTypeGroupEnum} from "../../../enums/dashboard/DataTypeUnitEnum";
import {VMTypeEnum} from "../../../models/virtualmetric/VirtualMetric";

@Component({
  selector: 'app-metric-selector',
  templateUrl: './metric-selector.component.html',
  styleUrls: ['./metric-selector.component.css']
})
export class MetricSelectorComponent extends BaseComponent implements AfterViewInit {

  /**
   * List containing all the partition devices
   * @protected
   */
  @Input() deviceList!: Device[];

  /**
   * List containing all the gateway-device metrics
   * @protected
   */
  @Input() metricList!: UserMetricInstance[];

  @Input() inputDisplayMode?: "table" | "list" | "dropdown" = "table";

  @Input() selectionMode: 'single' | 'multiple' | 'all' | 'none' = 'all';

  @Input() forbiddenDataTypes: DataTypeGroupEnum[] = [];

  @Input() forbidSVM: boolean = false;

  @Input() disabled: boolean = false;

  @Input() isDataForChart: boolean = false;

  @Input() forbidWriteable: boolean = false;

  @Output() public selectedMetricIdsChange: EventEmitter<number[]> = new EventEmitter();

  @Output() public listSelectionChanged: EventEmitter<any> = new EventEmitter();

  @Output() public selectedMetricsChange: EventEmitter<UserMetricInstance[]> = new EventEmitter();

  @Output() public newSelectedMetric: EventEmitter<UserMetricInstance> = new EventEmitter();
  @Input() selectedMetrics: UserMetricInstance[] = [];
  protected loadingData: boolean = false;
  /**
   * Array containing a list of default colors for the chart to init metrics
   * @protected
   */
  /**
   * Service containing the shared data and procedures among dashboard components
   * @protected
   */
  protected dashboardService = inject(DashboardService);
  protected virtualMetricOpeService = inject(VirtualmetricOpeService);
  /**
   * Service used for api calls related to devices
   * @protected
   */
  protected deviceOpeService = inject(DeviceOpeService);

  constructor() {
    super();
    this.metricList = [];
  }

  private _selectedPartitionId!: number | null;

  get selectedPartitionId(): number | null {
    return this._selectedPartitionId;
  }

  @Input() set selectedPartitionId(value: number | null) {
    this._selectedPartitionId = value;
    this.loadDeviceMetrics(this.selectedDeviceId, this.selectedPartitionId)
  }

  /**
   * List containing all the metrics that were added in the current user metric group
   * @protected
   */
  private _selectedMetricIds: number[] = [];

  get selectedMetricIds(): number[] {
    return this._selectedMetricIds;
  }

  @Input() set selectedMetricIds(value: (number | undefined)[]) {
    if (value)
      this._selectedMetricIds = value.filter((x) => x !== undefined) as number[];
    // this.emitFullItems()
  }

  private _selectedDeviceId!: number | null;

  get selectedDeviceId(): number | null {
    return this._selectedDeviceId;
  }

  @Input() set selectedDeviceId(value: number | null) {
    this._selectedDeviceId = value;
    this.loadDeviceMetrics(this.selectedDeviceId, this.selectedPartitionId)
  }

  ngAfterViewInit() {
    this.loadDeviceMetrics(this.selectedDeviceId, this.selectedPartitionId);
  }

  /**
   * Pushes the desired MetricInstance inside the UserMetricGroup.
   * Since the user can select multiple times the same metric instance this function also generates a unique alias to be used as a key later on.
   * @param $event
   */
  updateSelectedKeys = ($event: any) => {
    try {
      let dataToSave: UserMetricInstance = structuredClone($event.row.data);
      if (!!dataToSave.dataTypeGroup && this.forbiddenDataTypes.includes(dataToSave.dataTypeGroup)) {
        this.toastService.openErrorToastMessage(this.translate.instant("CHART_CREATION.DATA_TYPE_NOT_ALLOWED"));
        return;
      }
      dataToSave.alias = uuid.v4();
      let theDevice = this.deviceList.find(x => x.id === this.dashboardService.selectedDeviceIds[0]);
      if (theDevice && !theDevice.isVirtualMeasure) {
        dataToSave.deviceId = theDevice.id;
        dataToSave.deviceName = theDevice.name;
      }
      let min = this.selectedMetrics.length;
      let j = Math.floor(this.selectedMetrics.length / this.gmService.BASE_COLORS.length) + 1;

      for (let value of this.gmService.BASE_COLORS) {
        let count = this.selectedMetrics.filter(m => m.color === value).length;
        if (count < j && count < min) {
          dataToSave.color = value;
          min = this.selectedMetrics.filter(m => m.color === value).length;
        }
      }
      dataToSave.color ??= this.gmService.BASE_COLORS[0];
      if (dataToSave.dataTypeGroup === DataTypeGroupEnum.BOOLEAN) {
        dataToSave.chartType = "SQUARE_WAVE";

      }

      dataToSave.functionType = AggregateFunctionTypeEnum.MEAN;

      this.selectedMetrics.push(dataToSave);
      this.newSelectedMetric.emit(dataToSave);
      this.selectedMetricIdsChange.emit(this.selectedMetrics.map(x => x.id));
      this.selectedMetricsChange.emit(this.selectedMetrics);
      this.toastService.openSuccessToastMessage(this.translate.instant("CHART_CREATION.METRIC_ADDED"));
    } catch (error: any) {
      this.toastService.openErrorToastMessage(error);
    }
  };


  /**
   * Calls the getDevicesMetricInstances API in order to retrieve and show all the selected gateway-device's Metric Instances
   * @param deviceId
   * @param partitionId
   */
  async loadDeviceMetrics(deviceId: number | null, partitionId: number | null) {
    this.metricList = [];
    if (!!partitionId || partitionId === 0) {
      if (deviceId === -2) {
        let cb = new GetVirtualMetricInstancesCB();
        cb.i.partitionId = partitionId;
        this.loadingData = true;
        this.virtualMetricOpeService.getVirtualMetricInstances(cb).then(
          () => {
            this.mapMetricInstances(cb.o.metricInstances as UserMetricInstance[]);
            this.loadingData = false;
          }
        )
      } else if (!!deviceId || deviceId === 0) {
        let cb = new GetDevicesMetricInstancesCB();
        cb.i.deviceId = deviceId;
        cb.i.partitionId = partitionId;
        cb.i.isDataForChart = this.isDataForChart;
        this.loadingData = true;
        this.deviceOpeService.getDevicesMetricInstances(cb).then(
          () => {
            this.mapMetricInstances(cb.o.metricInstances as UserMetricInstance[]);
            this.loadingData = false;
          }).catch(() => {
          this.loadingData = false;
        })
      }
    }
  }

  mapMetricInstances(mis: UserMetricInstance[]) {
    if (this.forbidSVM) {
      mis = mis.filter(
        mi => {
          return mi.type === "DM" || mi.vmType === VMTypeEnum.RESETTABLE
        }
      )
    }
    mis.filter(
      mi => {
        if (mi.dataTypeGroup)
          return !this.forbiddenDataTypes.includes(mi.dataTypeGroup);
        else
          return false
      }
    ).forEach(
      mi => {
        let newML = new UserMetricInstance;
        newML.partitionId = mi.partitionId;
        newML.partitionName = mi.partitionName;
        newML.id = mi.id;
        newML.type = mi.type;
        newML.lastValueTs = mi.lastValueTs;
        newML.lastValue = mi.lastValue;
        newML.name = mi.name;
        newML.dataTypeGroup = mi.dataTypeGroup;
        newML.dataTypeGroupMax = mi.dataTypeGroupMax;
        newML.dataTypeGroupMin = mi.dataTypeGroupMin;
        newML.dataTypeGroupShowAsHex = mi.dataTypeGroupShowAsHex;
        newML.dataUnit = mi.dataUnit;
        newML.decimalDigits = mi.decimalDigits;
        newML.deviceId = mi.deviceId;
        newML.deviceName = mi.deviceName;
        newML.vmType = mi.vmType;
        newML.wm2eInfo = mi.wm2eInfo;
        newML.wm2eStatus = mi.wm2eStatus;
        newML.isWritableOnEdge = !!mi?.wm2eInfo?.originType;
        if (!(this.forbidWriteable && newML.isWritableOnEdge)) {
          this.metricList.push(newML);
        }
      });

    this.metricList.sort(
      (a, b) => {
        if (a.id !== undefined && b.id !== undefined)
          return a.id - b.id;
        else
          return -1;
      });

  }

  emitFullItems() {

    this.selectedMetricIds.forEach((item) => {
      if (this.selectedMetrics.findIndex(x => x.id === item) < 0) {
        let metricToPush = this.metricList.find(metric => metric.id === item);
        if (metricToPush) {
          this.selectedMetrics.push(metricToPush);
        }
      }
    });

    this.selectedMetrics.forEach((item, index) => {
      if (!this.selectedMetricIds.includes(item.id)) {
        this.selectedMetrics.splice(index, 1);
      }
    });

    this.selectedMetricsChange.emit(this.selectedMetrics);
  }

  checkForChangesAndEmit(currentValue: number[]) {
    if (!this.loadingData && !this.arrayCompare(currentValue, this.selectedMetricIds)) {
      this.selectedMetricIds = currentValue;
      this.selectedMetricIdsChange.emit(currentValue);
      this.emitFullItems();
    }
  }

  arrayCompare(array: any[], array2: any[]) {
    // if the other array is a falsy value, return
    if (!array)
      return false;
    // if the argument is the same array, we can be sure the contents are same as well
    if (array === array2)
      return true;
    // compare lengths - can save a lot of time
    if (array2.length != array.length)
      return false;

    for (let i = 0, l = array2.length; i < l; i++) {
      // Check if we have nested arrays
      if (array2[i] instanceof Array && array[i] instanceof Array) {
        // recurse into the nested arrays
        if (!array2[i].equals(array[i]))
          return false;
      } else if (array2[i] != array[i]) {
        // Warning - two different object instances will never be equal: {x:20} != {x:20}
        return false;
      }
    }
    return true;
  }

  parseLastValue = (row: UserMetricInstance) => {
    return this.appService.getParsedMetricValue(row);
  };

  resetSelectedMetrics = () => {
    this.selectedMetrics = [];
    this.selectedMetricIds = [];
    this.selectedMetricIdsChange.emit(this.selectedMetricIds);
    this.selectedMetricsChange.emit(this.selectedMetrics);
  };
}
